import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as billingService from '../../services/billingService';
import Toast from '../Toast';
import ConfirmDialog from '../ConfirmDialog';
import settingsStyles from '../../pages/dashboard/Settings.module.css';

function UsageRow({ icon, label, count, priceEach }) {
  const cost = (Number(count) || 0) * (Number(priceEach) || 0);
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'auto 1fr auto auto',
      gap: '14px', padding: '14px 0',
      borderBottom: '1px solid #f1f5f9', alignItems: 'center',
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: 'linear-gradient(135deg, #E6F1FB 0%, #C6DDF3 100%)',
        color: '#185FA5', display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '0.9375rem', color: '#0f172a', fontWeight: 500 }}>{label}</div>
        <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 2 }}>
          {count} × R{Number(priceEach).toFixed(0)}/month
        </div>
      </div>
      <span style={{
        fontSize: '1rem', fontWeight: 600, color: '#0f172a',
        minWidth: '90px', textAlign: 'right', fontVariantNumeric: 'tabular-nums',
      }}>
        R{cost.toFixed(2)}
      </span>
    </div>
  );
}

const ICON_USER = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);

const ICON_RESOURCE = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

export default function BillingManager() {
  const [subscription, setSubscription] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [updatingPayment, setUpdatingPayment] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [toast, setToast] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  useEffect(() => {
    const status = searchParams.get('status');
    if (status === 'success') {
      showToast('Payment successful! Your subscription is now active.', 'success');
      setSearchParams((prev) => { prev.delete('status'); return prev; });
    } else if (status === 'cancelled') {
      showToast('Payment was cancelled.', 'info');
      setSearchParams((prev) => { prev.delete('status'); return prev; });
    }
  }, [searchParams, setSearchParams, showToast]);

  useEffect(() => {
    billingService.getSubscription()
      .then(setSubscription)
      .catch(() => showToast('Failed to load billing information.', 'error'))
      .finally(() => setLoadingData(false));
  }, [showToast]);

  const handleCancel = async () => {
    setShowCancelConfirm(false);
    setCancelling(true);
    try {
      const result = await billingService.cancelSubscription();
      showToast(
        `Subscription cancelled. Access continues until ${new Date(result.access_until).toLocaleDateString('en-ZA')}.`,
        'success'
      );
      const sub = await billingService.getSubscription();
      setSubscription(sub);
    } catch {
      showToast('Failed to cancel subscription. Please try again.', 'error');
    } finally {
      setCancelling(false);
    }
  };

  const submitPayfastForm = (payfast_url, fields) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = payfast_url;
    Object.entries(fields).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
  };

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      const { payfast_url, fields } = await billingService.initiatePayment();
      submitPayfastForm(payfast_url, fields);
    } catch {
      showToast('Failed to initiate payment. Please try again.', 'error');
      setSubscribing(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    setUpdatingPayment(true);
    try {
      const { payfast_url, fields } = await billingService.updatePaymentMethod();
      submitPayfastForm(payfast_url, fields);
    } catch {
      showToast('Failed to start payment update. Please try again.', 'error');
      setUpdatingPayment(false);
    }
  };

  const renderStatusCard = () => {
    if (!subscription) return null;
    const { status, is_trial_active, days_remaining, cancel_at_period_end, current_period_end, amount } = subscription;

    const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

    let tone, label, headline, detail;
    if (status === 'trialing' && is_trial_active) {
      tone = { bg: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)', accent: '#B45309', text: '#78350F', dot: '#F59E0B' };
      label = 'Trial';
      headline = `${days_remaining} day${days_remaining !== 1 ? 's' : ''} remaining`;
      detail = 'Subscribe before your trial ends to keep full access.';
    } else if (status === 'past_due') {
      tone = { bg: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)', accent: '#B91C1C', text: '#7F1D1D', dot: '#EF4444' };
      label = 'Action required';
      headline = 'Payment failed';
      detail = 'Your last payment could not be processed. Update your payment method to restore access.';
    } else if (status === 'active' && cancel_at_period_end && current_period_end) {
      tone = { bg: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)', accent: '#B45309', text: '#78350F', dot: '#F59E0B' };
      label = 'Cancellation scheduled';
      headline = `Access ends ${fmtDate(current_period_end)}`;
      detail = 'You have full access until then. Resume to keep your team running.';
    } else if (status === 'active') {
      tone = { bg: 'linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)', accent: '#047857', text: '#064E3B', dot: '#10B981' };
      label = 'Active';
      headline = current_period_end ? `Next charge ${fmtDate(current_period_end)}` : 'Your subscription is active';
      detail = `R${Number(amount || 0).toFixed(2)} will be billed on renewal.`;
    } else {
      tone = { bg: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)', accent: '#B91C1C', text: '#7F1D1D', dot: '#EF4444' };
      label = 'Inactive';
      headline = 'No active subscription';
      detail = 'Your account is in read-only mode. Subscribe below to restore full access.';
    }

    return (
      <div style={{
        background: tone.bg, border: `1px solid ${tone.accent}33`, borderRadius: 16,
        padding: '20px 22px', marginBottom: 20,
        display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap',
      }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 10px', borderRadius: 999, background: 'rgba(255,255,255,0.65)',
          color: tone.accent, fontSize: '0.6875rem', fontWeight: 700,
          letterSpacing: '0.06em', textTransform: 'uppercase', flexShrink: 0,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: tone.dot }} />
          {label}
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: '1.0625rem', fontWeight: 700, color: tone.text, letterSpacing: '-0.01em' }}>
            {headline}
          </div>
          <div style={{ fontSize: '0.875rem', color: tone.text, opacity: 0.85, marginTop: 2, lineHeight: 1.5 }}>
            {detail}
          </div>
        </div>
      </div>
    );
  };

  const renderCostBreakdown = () => {
    if (!subscription) return null;
    const {
      pricing, userCount, totalResources, amount,
      appliedMinimum, minimum, pending_balance, current_period_end,
    } = subscription;
    if (!pricing) return null;

    const pendingBalance = Number(pending_balance || 0);
    const nextInvoice = (Number(amount) + pendingBalance).toFixed(2);

    return (
      <div className={settingsStyles.card}>
        <h3>Current usage</h3>
        <p className={settingsStyles.cardDesc}>
          Billed per user and per resource or contractor · updates each renewal.
        </p>

        <div>
          <UsageRow
            icon={ICON_USER}
            label={`Users${userCount > 0 ? ` · ${userCount} total` : ''}`}
            count={userCount}
            priceEach={pricing.price_per_user}
          />
          <UsageRow
            icon={ICON_RESOURCE}
            label={`Resources & contractors${totalResources > 0 ? ` · ${totalResources} total` : ''}`}
            count={totalResources}
            priceEach={pricing.price_per_resource}
          />
        </div>

        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
          marginTop: 18, paddingTop: 18, borderTop: '1px solid #e2e8f0',
        }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
              Base monthly total
            </div>
            {appliedMinimum && (
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 4 }}>
                Minimum charge of R{minimum} applied
              </div>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{
              fontWeight: 700, fontSize: '1.75rem', color: '#0f172a',
              letterSpacing: '-0.025em', lineHeight: 1, fontVariantNumeric: 'tabular-nums',
            }}>
              R{Number(amount).toFixed(2)}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 4 }}>per month</div>
          </div>
        </div>

        {pendingBalance > 0 && (
          <>
            <div style={{
              marginTop: 14, padding: '12px 14px', borderRadius: 10,
              background: '#FEF9EC', border: '1px solid #FCD34D',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#92400E' }}>
                  Pending proration charge
                </div>
                <div style={{ fontSize: '0.75rem', color: '#B45309', marginTop: 2 }}>
                  Seats added mid-cycle · collected at next renewal
                  {current_period_end && ` (${new Date(current_period_end).toLocaleDateString('en-ZA')})`}
                </div>
              </div>
              <span style={{ fontWeight: 700, fontSize: '1rem', color: '#92400E', fontVariantNumeric: 'tabular-nums' }}>
                +R{pendingBalance.toFixed(2)}
              </span>
            </div>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginTop: 10, paddingTop: 10, borderTop: '1px dashed #e2e8f0',
            }}>
              <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#475569' }}>
                Next invoice estimate
              </span>
              <span style={{ fontWeight: 700, fontSize: '1.125rem', color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                R{nextInvoice}
              </span>
            </div>
          </>
        )}

        <div style={{
          marginTop: 14, paddingTop: 14, borderTop: '1px solid #f1f5f9',
          fontSize: '0.75rem', color: '#94a3b8', lineHeight: 1.5,
        }}>
          R{Number(pricing.price_per_user).toFixed(0)}/user · R{Number(pricing.price_per_resource).toFixed(0)}/resource or contractor · R{minimum} minimum
        </div>
      </div>
    );
  };

  if (loadingData) {
    return <div className={settingsStyles.sectionContent}><p>Loading billing information...</p></div>;
  }

  const isActive = subscription?.status === 'active';
  const isCancelScheduled = isActive && subscription?.cancel_at_period_end;

  return (
    <div className={settingsStyles.sectionContent}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {showCancelConfirm && (
        <ConfirmDialog
          message="Are you sure you want to cancel your subscription? You will keep access until the end of the current billing period."
          confirmText="Cancel Subscription"
          cancelText="Keep Subscription"
          onConfirm={handleCancel}
          onCancel={() => setShowCancelConfirm(false)}
        />
      )}

      <div className={settingsStyles.sectionHeader}>
        <h2 className={settingsStyles.sectionTitle}>Billing & Subscription</h2>
        <p className={settingsStyles.sectionDescription}>
          Usage-based pricing — pay only for what you use.
        </p>
      </div>

      {renderStatusCard()}
      {renderCostBreakdown()}

      {!isActive && (
        <div className={settingsStyles.card}>
          <h3>Start your subscription</h3>
          <p className={settingsStyles.cardDesc}>
            Restore full access. You will be redirected to PayFast to enter your card details.
          </p>
          <button
            onClick={handleSubscribe}
            disabled={subscribing}
            style={{
              padding: '12px 28px', borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg, #185FA5 0%, #0C447C 100%)',
              color: 'white', fontSize: '0.9375rem', fontWeight: 600,
              cursor: subscribing ? 'not-allowed' : 'pointer', opacity: subscribing ? 0.7 : 1,
              minWidth: 220, boxShadow: '0 4px 12px rgba(24, 95, 165, 0.25)',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            }}
            onMouseEnter={e => { if (!subscribing) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(24, 95, 165, 0.32)'; } }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(24, 95, 165, 0.25)'; }}
          >
            {subscribing ? 'Redirecting…' : `Subscribe · R${Number(subscription?.amount || 0).toFixed(2)}/month`}
          </button>
        </div>
      )}

      {isActive && (
        <div className={settingsStyles.card}>
          <h3>Manage subscription</h3>
          <p className={settingsStyles.cardDesc}>
            Replace your card, or cancel your subscription. Cancelling keeps access until your current period ends.
          </p>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12,
          }}>
            <ManageAction
              icon={ICON_CARD}
              title="Update payment method"
              description="Replace the card on file via PayFast."
              buttonLabel={updatingPayment ? 'Redirecting…' : 'Update card'}
              onClick={handleUpdatePaymentMethod}
              disabled={updatingPayment}
            />
            {!isCancelScheduled && (
              <ManageAction
                icon={ICON_CANCEL}
                title="Cancel subscription"
                description="Access continues until your current period ends."
                buttonLabel={cancelling ? 'Cancelling…' : 'Cancel'}
                onClick={() => setShowCancelConfirm(true)}
                disabled={cancelling}
                danger
              />
            )}
          </div>
        </div>
      )}

      <div className={settingsStyles.card} style={{
        background: 'linear-gradient(135deg, #F8FAFC 0%, #E6F1FB 100%)',
      }}>
        <h3>Need custom pricing?</h3>
        <p className={settingsStyles.cardDesc}>
          For volume discounts or enterprise arrangements, get in touch.
        </p>
        <a
          href="mailto:sales@nodel.co.za"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none',
            padding: '10px 20px', borderRadius: 10,
            border: '1px solid #185FA5', background: 'white',
            color: '#185FA5', fontSize: '0.875rem', fontWeight: 600,
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#185FA5'; e.currentTarget.style.color = 'white'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = '#185FA5'; }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
          Contact sales
        </a>
      </div>
    </div>
  );
}

const ICON_CARD = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="20" height="14" rx="3" /><line x1="2" y1="10" x2="22" y2="10" />
  </svg>
);

const ICON_CANCEL = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

function ManageAction({ icon, title, description, buttonLabel, onClick, disabled, danger }) {
  const accent = danger ? '#EF4444' : '#185FA5';
  const accentBg = danger ? '#FEE2E2' : '#E6F1FB';
  const hoverBg = danger ? '#FEF2F2' : '#F8FAFC';
  return (
    <div style={{
      border: '1px solid #e2e8f0', borderRadius: 12, padding: 16,
      display: 'flex', flexDirection: 'column', gap: 10, background: 'white',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: accentBg, color: accent,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          {icon}
        </div>
        <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#0f172a' }}>{title}</div>
      </div>
      <div style={{ fontSize: '0.8125rem', color: '#64748b', lineHeight: 1.5 }}>
        {description}
      </div>
      <button
        onClick={onClick}
        disabled={disabled}
        style={{
          padding: '8px 16px', borderRadius: 8, border: `1px solid ${accent}`,
          background: 'white', color: accent, fontSize: '0.8125rem', fontWeight: 600,
          cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
          alignSelf: 'flex-start', transition: 'all 0.15s',
        }}
        onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = hoverBg; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}
      >
        {buttonLabel}
      </button>
    </div>
  );
}
